// kanjo/Sales.jsx — Daily sales close wired to live channels + entries (US-4.1).
// Style aligned with the mockup design system: hero net total + sparkline +
// per-channel row with vendor-color avatar.

function fmtMoneyS(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' })
    .format((c || 0) / 100);
}
function moneyPartsS(cents, currency, locale) {
  return moneyParts(cents, currency, locale);
}

// ── HourlySparkS: delegates to the Today screen's honest bar renderer.
// Sales close does not yet ship per-hour data, so we surface that explicitly
// rather than synthesise a curve.
function HourlySparkS({ buckets, color = 'var(--positive)' }) {
  return <HourlySpark buckets={buckets || []} color={color}/>;
}

// ── Channel avatar: first letter on a consistent per-channel color ────────
const CHANNEL_PALETTE = ['#0E7C5C', '#1F3A68', '#B45309', '#7A5AE0', '#B92517', '#0a8a8c', '#605c52', '#314550'];
// Mix userId into the hash so two restaurants don't get the same color for
// the same channel name — gives each operator a distinct palette.
function channelColor(channelId, userId = '') {
  const key = String(userId) + '|' + String(channelId);
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return CHANNEL_PALETTE[h % CHANNEL_PALETTE.length];
}

// Shift a 'YYYY-MM-DD' date by n days (UTC-safe).
function shiftIsoDate(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function useSalesClose() {
  const [state, setState] = React.useState({ loading: true });
  const load = React.useCallback(async (date) => {
    if (!window.kanjo) return;
    try {
      const claims = await window.kanjo.auth.currentClaims().catch(() => null);
      if (!claims) { setState({ loading: false }); return; }
      const profile = await window.kanjo.db.userProfile.get(claims.sub);
      await window.kanjo.revenue.channels.ensureDefaults();
      // Deposits = revenue: auto-derive sales from imported bank deposits before
      // reading the day. Idempotent; never clobbers a manual close.
      try { await window.kanjo.revenue.sales.syncFromDeposits(); }
      catch (e) { console.warn('[Sales] deposit sync failed', e); }
      const d = date || localToday();
      const breakdown = await window.kanjo.revenue.sales.dayBreakdown(d);
      setState({
        loading: false, date: d, breakdown,
        today: localToday(),
        currency: profile?.currency || 'USD',
        locale: profile?.locale || 'en-US',
        userId: claims.sub,
      });
    } catch (e) { console.warn('[Sales] load failed', e); setState({ loading: false }); }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  return [state, setState, load];
}

function SalesClose({ dark = false, persona = 'owner' }) {
  useT();
  const [state, setState, reload] = useSalesClose();
  const [drafts, setDrafts] = React.useState({});
  // Must be declared before any early return — React requires a stable hook
  // order across renders (the loading branch returns early below).
  const [closing, setClosing] = React.useState(false);
  const [cfgOpen, setCfgOpen] = React.useState(false);

  if (state.loading) return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <KHeader back big title={t('sales.closeTitle', 'Sales close')} />
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SkeletonCard height={200}/>
        <SkeletonCard height={140}/>
      </div>
    </KShell>
  );

  const totalNet = state.breakdown.reduce((s, r) => s + (r.netSalesCents || 0), 0);
  const totalGross = state.breakdown.reduce((s, r) => s + (r.grossSalesCents || 0), 0);
  const totalFees = state.breakdown.reduce((s, r) => s + (r.platformFeesCents || 0), 0);
  const totalRefunds = state.breakdown.reduce((s, r) => s + (r.refundsCents || 0) + (r.voidsCents || 0) + (r.compsCents || 0), 0);
  const m = moneyPartsS(totalNet, state.currency, state.locale);

  function setGross(channelId, valueStr) {
    const v = Number(valueStr);
    setDrafts((d) => ({ ...d, [channelId]: { gross: valueStr, valid: Number.isFinite(v) && v >= 0 } }));
  }

  const dirty = Object.entries(drafts).filter(([, d]) => d && d.valid && d.gross !== '');
  async function closeOut() {
    if (closing || dirty.length === 0) return;
    setClosing(true);
    try {
      for (const [channelId, d] of dirty) {
        await window.kanjo.revenue.sales.recordClose({
          channelId, date: state.date, grossSales: Number(d.gross),
        });
      }
      setDrafts({});
      window.kanjoToast?.({ kind: 'success', text: dirty.length === 1
        ? t('sales.closedOne', 'Closed 1 channel.')
        : t('sales.closedMany', 'Closed {n} channels.').replace('{n}', String(dirty.length)) });
      reload(state.date);
    } catch (e) {
      console.warn('[Sales] close-out failed', e);
      window.kanjoToast?.({ kind: 'error', text: t('sales.saveFailed', 'Some channels failed to save. Retry.') });
    } finally {
      setClosing(false);
    }
  }

  const goDay = (delta) => { setDrafts({}); reload(shiftIsoDate(state.date, delta)); };
  const atToday = state.date >= (state.today || state.date);

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title={t('sales.closeTitle', 'Sales close')} subtitle={state.date}
          right={<GearButton label={t('settings.deposits', 'Deposit recognition settings')} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => setCfgOpen(false)} title={t('settings.deposits', 'Deposit recognition')}>
          <DepositKeywordSettings />
        </Sheet>

        {/* Day navigation — revenue auto-fills from imported bank deposits */}
        <div style={{ padding: '0 20px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => goDay(-1)} aria-label={t('sales.prevDay', 'Previous day')}
                  style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink)', cursor: 'pointer', fontSize: 15 }}>‹</button>
          <button onClick={() => goDay(1)} disabled={atToday} aria-label={t('sales.nextDay', 'Next day')}
                  style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid var(--rule)', background: 'var(--surface)', color: atToday ? 'var(--ink-3)' : 'var(--ink)', cursor: atToday ? 'default' : 'pointer', fontSize: 15 }}>›</button>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--on-dim)' }}>{t('sales.autoFilled', 'Auto-filled from deposits')}</span>
        </div>

        {/* Hero net total + sparkline */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div className="label">{atToday ? t('sales.netToday', 'Net today') : t('sales.net', 'Net')}</div>
              {totalNet > 0 && (
                <span className="pill positive">
                  <span className="dot" style={{ background: 'currentColor' }}/>
                  {t('sales.live', 'LIVE')}
                </span>
              )}
            </div>
            <div className={`figure xl ${totalNet === 0 ? 'sm-zero' : ''}`} style={{ marginTop: 10 }}>
              {renderMoney(m)}
              {totalNet === 0 && <span className="figure suffix">{t('sales.noSalesYet', 'No sales yet')}</span>}
            </div>
            <div style={{ marginTop: 16 }}>
              <HourlySparkS buckets={[]}/>
            </div>
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--rule)',
                          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
              <div>
                <div className="label">{t('sales.gross', 'Gross')}</div>
                <div className="mono" style={{ fontWeight: 500, marginTop: 2 }}>{fmtMoneyS(totalGross, state.currency, state.locale)}</div>
              </div>
              <div>
                <div className="label">{t('sales.platformFees', 'Platform fees')}</div>
                <div className="mono" style={{ fontWeight: 500, marginTop: 2, color: 'var(--warning)' }}>−{fmtMoneyS(totalFees, state.currency, state.locale)}</div>
              </div>
              <div>
                <div className="label">{t('sales.refunds', 'Refunds/voids/comps')}</div>
                <div className="mono" style={{ fontWeight: 500, marginTop: 2 }}>{fmtMoneyS(totalRefunds, state.currency, state.locale)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* By channel */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>{t('sales.byChannel', 'By channel')}</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {state.breakdown.map((r, i, a) => {
              const draft = drafts[r.channelId];
              const initial = (r.channelName || '?')[0].toUpperCase();
              const color = channelColor(r.channelId, state.userId);
              const feesPct = (r.commissionRateBps || 0) / 100;
              return (
                <div key={r.channelId} style={{
                  display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 12,
                  padding: '14px 16px', alignItems: 'center',
                  borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 600, flexShrink: 0,
                  }}>{initial}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{r.channelName}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
                      {feesPct > 0 ? `${feesPct}% ${t('sales.fees', 'fees')} · ` : ''}
                      {t('sales.net', 'Net')} <span className="mono">{fmtMoneyS(r.netSalesCents, state.currency, state.locale)}</span>
                      {r.lastCloseCents != null && (
                        <> · <span style={{ color: 'var(--ink-3)' }}>{t('sales.lastClose', 'last')} {fmtMoneyS(r.lastCloseCents, state.currency, state.locale)}</span></>
                      )}
                    </div>
                  </div>
                  <input
                    inputMode="decimal"
                    placeholder={r.hasEntry ? (r.grossSalesCents / 100).toFixed(2) : t('sales.grossPlaceholder', 'Gross $')}
                    aria-label={t('sales.grossSalesFor', 'Gross sales for {name}').replace('{name}', r.channelName)}
                    value={draft?.gross ?? ''}
                    onChange={(e) => setGross(r.channelId, e.target.value)}
                    style={{
                      width: 92, padding: '8px 10px', borderRadius: 10,
                      border: '1px solid var(--rule)', background: 'var(--surface)',
                      fontFamily: 'var(--mono)', fontSize: 14, textAlign: 'right',
                      color: 'var(--ink)',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Bulk close-out CTA */}
        <div style={{ padding: '0 20px 28px' }}>
          <button onClick={closeOut} disabled={closing || dirty.length === 0}
                  className="btn primary lg"
                  style={{ width: '100%', opacity: (closing || dirty.length === 0) ? 0.6 : 1, cursor: closing ? 'default' : 'pointer' }}>
            {closing ? t('sales.saving', 'Saving…')
              : dirty.length === 0 ? t('sales.closeOut', 'Close out')
              : dirty.length === 1 ? t('sales.closeOutOne', 'Close out 1 channel')
              : t('sales.closeOutMany', 'Close out {n} channels').replace('{n}', String(dirty.length))}
          </button>
        </div>

        <div style={{ padding: '0 20px' }}>
          <SalesBasisNote />
        </div>
      </div>
    </KShell>
  );
}

// ─── What these numbers actually are ─────────────────────────────────────────
// Sales on this tab are reverse-engineered from bank deposits, which means both
// the DATE and the AMOUNT differ from what an accountant would book: the date is
// when the processor paid, not when the food was sold, and the amount is the
// payout after fees are withheld. That's the right basis for "can I make
// payroll" and the wrong basis for a tax return, so the screen says so rather
// than leaving the operator to discover it against their POS totals.
// Rendered at the bottom of BOTH sales screens.
function SalesBasisNote() {
  useT();
  const bullet = { display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 8 };
  const dot = { flexShrink: 0, width: 4, height: 4, borderRadius: 2, background: 'var(--warning)', marginTop: 7 };
  const body = { fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 };
  // No horizontal padding of its own — both callers already sit inside a
  // 20px gutter, and nesting the two would indent this card past every other.
  return (
    <div style={{ margin: '10px 0 12px' }}>
      <div className="card" style={{ padding: 16, borderLeft: '3px solid var(--warning)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          {t('sales.basis.title', 'Where these numbers come from')}
        </div>
        <div style={body}>
          {t('sales.basis.intro', 'Sales here are derived from money landing in your bank account, not from your POS.')}
        </div>
        <div style={bullet}><span style={dot}/><div style={body}>
          {t('sales.basis.date', "The date is the deposit date, not the day you sold. Card batches settle a day or two later and delivery platforms pay weekly, so Saturday's business usually appears on Tuesday — and nothing settles at the weekend.")}
        </div></div>
        <div style={bullet}><span style={dot}/><div style={body}>
          {t('sales.basis.amount', 'The amount is what the processor paid you, after fees, refunds and adjustments are withheld. It is not gross sales, and cash you never deposited never appears.')}
        </div></div>
        <div style={{ ...body, marginTop: 10 }}>
          {t('sales.basis.closing', "These figures describe cash flow, not revenue. They do not follow revenue-recognition rules, they will not agree with your POS totals or your books, and they must never be used for tax filing, sales-tax returns or financial statements — use your POS reports and your accountant's records for those.")}
        </div>
      </div>
    </div>
  );
}

// ─── Sales trends — deposits over time (7/30-day vs prior, by weekday) ────────
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Month / weekday names follow the APP language (Account -> Language), NOT the
// book's currency locale: a Spanish UI on an en-US book should still read
// "mar", not "Mar". Money keeps the profile locale — that one is about number
// and currency conventions, not words.
function uiLocale() {
  try { return getLangMeta(getActiveLang()).locale || 'en-US'; } catch { return 'en-US'; }
}

// "Mon D" label for the start of a 7-day week given its end ISO date.
function fmtWeekStart(endIso) {
  const d = new Date(endIso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - 6);
  return d.toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// "Mon, May 26" label for a daily breakdown row.
function fmtDayLabel(iso) {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(uiLocale(),
    { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Bucket labels for the YTD chart — derived from startIso so the bucket engine
// itself can stay language-free. Short form for the axis, long for the readout.
function bucketLabel(b, gran) {
  const d = new Date(b.startIso + 'T00:00:00Z');
  if (gran === 'quarter') return t('sales.quarterN', 'Q{q}').replace('{q}', String(Math.floor(d.getUTCMonth() / 3) + 1));
  if (gran === 'month') return d.toLocaleDateString(uiLocale(), { month: 'short', timeZone: 'UTC' });
  return d.toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
function bucketFullLabel(b, gran) {
  const d = new Date(b.startIso + 'T00:00:00Z');
  const y = String(d.getUTCFullYear());
  if (gran === 'quarter') return t('sales.quarterYear', 'Q{q} {y}')
    .replace('{q}', String(Math.floor(d.getUTCMonth() / 3) + 1)).replace('{y}', y);
  if (gran === 'month') return d.toLocaleDateString(uiLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return t('sales.weekOf', 'Week of {d}').replace('{d}',
    d.toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }));
}

// ── Year-to-date trend chart ──────────────────────────────────────────────
// Bars = this year so far, bucketed by quarter / month / week (toggle). When
// last year's data exists, the same periods a year back are drawn as a dashed
// reference line ON TOP of the bars — the in-progress bucket's twin is
// truncated to the same elapsed point, so "week to date" never loses to a full
// week (lib/revenue/salesBuckets.js owns that math).
//
// Bars are coloured by their parent period (weeks take their month's hue,
// months take their quarter's) from the --series-1..6 categorical family, so
// the year reads as blocks of time instead of one undifferentiated wall.
const CHART_H = 104;

function SeriesSwatch({ n, size = 9 }) {
  return <span style={{ width: size, height: size, borderRadius: 2, background: `var(--series-${n})`, display: 'inline-block' }}/>;
}

function YtdTrendChart({ ytd, currency, locale, year }) {
  useT();
  const [gran, setGran] = React.useState('month');
  const [metric, setMetric] = React.useState('total');
  const [sel, setSel] = React.useState(null); // selected bucket index

  const view = ytd?.[gran];
  React.useEffect(() => { setSel(null); }, [gran, metric]);
  if (!view || view.buckets.length === 0) return null;

  const buckets = view.buckets;
  const pick = (b) => metric === 'delivery' ? b.delivery : metric === 'inHouse' ? b.dineIn : b.total;
  const pickPrior = (b) => metric === 'delivery' ? b.priorDelivery : metric === 'inHouse' ? b.priorDineIn : b.priorTotal;
  const series = buckets.map(pick);
  const prior = buckets.map(pickPrior);
  if (series.every((v) => v === 0) && prior.every((v) => v === 0)) return null;

  // A year-over-year number is only meaningful where BOTH years are recorded.
  // `priorComparable` marks the buckets whose prior-year window sits entirely
  // inside the book's history; everything below compares over that subset only.
  // Without this, a book that starts in March reports the first half up 60%
  // purely because January and February of last year are missing.
  const cmpIdx = buckets.reduce((a, b, i) => (b.priorComparable ? (a.push(i), a) : a), []);
  const showPrior = !!view.hasPrior && cmpIdx.length > 0;
  const max = Math.max(1, ...series, ...(showPrior ? cmpIdx.map((i) => prior[i]) : []));
  const hue = (b) => `var(--series-${(b.group % 6) + 1})`;
  const fmt = (c) => fmtMoneyS(c, currency, locale);
  const n = buckets.length;

  // The headline stays the TRUE year to date. The comparison is scoped to the
  // comparable buckets and says so when that's a shorter span.
  const ytdNow = series.reduce((s, v) => s + v, 0);
  const cmpNow = cmpIdx.reduce((s, i) => s + series[i], 0);
  const cmpPrior = cmpIdx.reduce((s, i) => s + prior[i], 0);
  const ytdPct = showPrior && cmpPrior > 0 ? ((cmpNow - cmpPrior) / cmpPrior) * 100 : null;
  const partialCmp = showPrior && cmpIdx.length < n;
  const cmpRange = partialCmp
    ? `${bucketLabel(buckets[cmpIdx[0]], gran)}–${bucketLabel(buckets[cmpIdx[cmpIdx.length - 1]], gran)}`
    : null;

  // Prior-year polyline in a 0..100 box; it breaks wherever the prior year
  // isn't recorded, rather than dropping to a zero that reads as a collapse.
  const pts = buckets.map((b, i) => (b.priorComparable
    ? { x: ((i + 0.5) / n) * 100, y: 100 - (prior[i] / max) * 100 }
    : null));
  const segments = [];
  let run = [];
  for (const p of pts) {
    if (p) run.push(p);
    else { if (run.length) segments.push(run); run = []; }
  }
  if (run.length) segments.push(run);

  const detail = sel != null ? buckets[sel] : null;
  const detailV = detail ? pick(detail) : 0;
  const detailP = detail ? pickPrior(detail) : 0;
  // Per-bucket delta only where that bucket's own prior window is recorded.
  const detailCmp = !!detail && !!detail.priorComparable;
  const detailPct = detailCmp && detailP > 0 ? ((detailV - detailP) / detailP) * 100 : null;

  const GRANS = [['quarter', t('sales.granQuarter', 'Quarter')],
                 ['month', t('period.month', 'Month')],
                 ['week', t('period.week', 'Week')]];
  // Spelled out rather than keyed off `gran` so every string stays statically
  // greppable — test/i18n.test.js only sees literal t() keys.
  const byGranLabel = (gran === 'quarter' ? t('sales.byQuarter', '{y}, by quarter')
                     : gran === 'month'   ? t('sales.byMonth', '{y}, by month')
                     :                      t('sales.byWeek', '{y}, by week')).replace('{y}', String(year));
  const seg = (opts, value, onPick, ariaLabel, small) => (
    <div role="tablist" aria-label={ariaLabel} style={{ display: 'flex', gap: 4, padding: 3,
      background: 'var(--surface-3,#f0f0f3)', borderRadius: 8, marginBottom: 10 }}>
      {opts.map(([k, lbl]) => {
        const on = value === k;
        return (
          <button key={k} role="tab" aria-selected={on} onClick={() => onPick(k)}
            style={{ flex: 1, padding: small ? '6px 8px' : '7px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: small ? 12 : 12.5, fontWeight: 600,
              background: on ? 'var(--surface,#fff)' : 'transparent',
              color: on ? 'var(--ink)' : 'var(--ink-3)',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{lbl}</button>
        );
      })}
    </div>
  );

  return (
    <div className="card" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="label">{t('sales.ytdTitle', 'Sales · {y} to date').replace('{y}', String(year))}</div>
        {ytdPct != null && (
          <span style={{ fontSize: 12, fontWeight: 700,
            color: ytdPct >= 0 ? 'var(--positive)' : 'var(--danger)' }}>
            {ytdPct >= 0 ? '▲' : '▼'} {fmtPct1(Math.abs(ytdPct))} {t('sales.vsYear', 'vs {y}').replace('{y}', String(year - 1))}
          </span>
        )}
      </div>

      {seg(GRANS, gran, setGran, t('sales.bucketSize', 'Bucket size'))}
      {seg([['total', t('sales.colTotal', 'Total')],
            ['inHouse', t('today.inHouse', 'In-house')],
            ['delivery', t('today.delivery', 'Delivery')]],
      metric, setMetric, t('sales.segmentLabel', 'Sales segment'), true)}

      {/* Readout: the tapped bucket, or the YTD total when nothing is tapped.
          Tap-to-read replaces hover tooltips — this app is phone-first. */}
      <div style={{ minHeight: 40, marginBottom: 8 }}>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
          {detail
            ? `${bucketFullLabel(detail, gran)}${detail.partial ? ` · ${t('sales.toDate', 'to date')}` : ''}`
            : t('sales.ytdToDate', '{y} to date').replace('{y}', String(year))}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{fmt(detail ? detailV : ytdNow)}</span>
          {/* The comparison is shown only where it is real. With nothing
              selected and partial coverage it is scoped to the comparable span
              and carries BOTH of that span's figures, so the reader is never
              invited to divide the headline by a shorter prior year. */}
          {(detail ? detailCmp : showPrior) && (
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              {!detail && partialCmp && (
                <>{cmpRange}: <span className="mono">{fmt(cmpNow)}</span> </>
              )}
              {/* Split around {v} so the money keeps its .mono treatment while
                  the words around it stay one translatable phrase. */}
              {(() => {
                const [pre, post] = t('sales.vsIn', 'vs {v} in {y}')
                  .replace('{y}', String(year - 1)).split('{v}');
                return (<>{pre}<span className="mono">{fmt(detail ? detailP : cmpPrior)}</span>{post}</>);
              })()}
              {(detail ? detailPct : ytdPct) != null && (
                <span style={{ marginLeft: 5, fontWeight: 700,
                  color: (detail ? detailPct : ytdPct) >= 0 ? 'var(--positive)' : 'var(--danger)' }}>
                  {(detail ? detailPct : ytdPct) >= 0 ? '▲' : '▼'} {fmtPct1(Math.abs(detail ? detailPct : ytdPct))}
                </span>
              )}
            </span>
          )}
          {/* Say plainly why a comparison is absent, rather than showing nothing. */}
          {detail && !detailCmp && view.hasPrior && (
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              {t('sales.noPriorPeriod', 'No {y} data for this period.').replace('{y}', String(year - 1))}
            </span>
          )}
        </div>
      </div>

      {/* Bars + prior-year overlay share one box so their scales line up.
          direction:ltr is load-bearing, not cosmetic: under dir="rtl" (Arabic)
          the flex bar row reverses but the SVG polyline and the %-positioned
          markers do NOT, which lands last year's line on the wrong bars. The
          plot area is a number line — pin it LTR (oldest → newest) in every
          language and everything agrees. The tick row below matches it. */}
      <div style={{ position: 'relative', height: CHART_H, direction: 'ltr' }}>
        <div style={{ display: 'flex', gap: n > 20 ? 2 : 4, alignItems: 'flex-end', height: '100%' }}>
          {buckets.map((b, i) => {
            const v = series[i];
            const h = v > 0 ? Math.max(3, Math.round((v / max) * CHART_H)) : 2;
            const on = sel === i;
            return (
              <button key={b.key} onClick={() => setSel(on ? null : i)} aria-pressed={on}
                aria-label={`${bucketFullLabel(b, gran)}: ${fmt(v)}`}
                style={{ flex: 1, height: '100%', padding: 0, border: 'none', background: 'transparent',
                  display: 'flex', alignItems: 'flex-end', cursor: 'pointer' }}>
                <span style={{ width: '100%', height: h, borderRadius: 2,
                  background: v > 0 ? hue(b) : 'var(--surface-3)',
                  opacity: b.partial ? 0.55 : 1,
                  outline: on ? '2px solid var(--ink)' : 'none', outlineOffset: 1 }}/>
              </button>
            );
          })}
        </div>
        {showPrior && (
          <>
            {/* The viewBox is stretched (preserveAspectRatio="none") so the
                0–100 grid maps onto the bar box exactly. Stroke width is held
                constant with vector-effect; ROUND markers can't survive that
                stretch, so they're HTML dots positioned in the same %-space. */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
              {/* Casing first: a solid card-coloured stroke under the dash, so
                  the line stays legible wherever it crosses a saturated bar. */}
              {segments.map((s, i) => (
                <polyline key={`c${i}`} fill="none" stroke="var(--surface)" strokeWidth="4"
                  vectorEffect="non-scaling-stroke" strokeLinejoin="round" opacity="0.85"
                  points={s.map((p) => `${p.x},${p.y}`).join(' ')}/>
              ))}
              {segments.map((s, i) => (
                <polyline key={i} fill="none" stroke="var(--ink-2)" strokeWidth="1.5" strokeDasharray="4 3"
                  vectorEffect="non-scaling-stroke" strokeLinejoin="round"
                  points={s.map((p) => `${p.x},${p.y}`).join(' ')}/>
              ))}
            </svg>
            {n <= 14 && pts.map((p, i) => p && (
              <span key={i} aria-hidden="true" style={{ position: 'absolute',
                left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%, -50%)',
                width: 7, height: 7, borderRadius: '50%', pointerEvents: 'none',
                background: 'var(--surface)', border: '1.5px solid var(--ink-2)' }}/>
            ))}
          </>
        )}
      </div>

      {/* X ticks — weeks label the first bucket of each month; months/quarters
          label every bucket. */}
      <div style={{ display: 'flex', gap: n > 20 ? 2 : 4, marginTop: 6, fontSize: 10.5,
        color: 'var(--ink-3)', direction: 'ltr' }}>
        {buckets.map((b, i) => {
          const show = gran !== 'week' || i === 0 || b.group !== buckets[i - 1].group;
          return (
            <div key={b.key} style={{ flex: 1, textAlign: gran === 'week' ? 'left' : 'center', overflow: 'visible', whiteSpace: 'nowrap' }}>
              {show ? bucketLabel(b, gran === 'week' ? 'month' : gran) : ''}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap',
        fontSize: 11, color: 'var(--ink-2)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {[1, 2, 3, 4].map((k) => <SeriesSwatch key={k} n={k} size={8}/>)}
          <span>{byGranLabel}</span>
        </span>
        {showPrior && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <svg width="18" height="8" aria-hidden="true"><line x1="0" y1="4" x2="18" y2="4"
              stroke="var(--ink-2)" strokeWidth="1.5" strokeDasharray="4 3"/></svg>
            <span>{t('sales.samePeriods', '{y}, same periods').replace('{y}', String(year - 1))}</span>
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}>{t('sales.tapBar', 'Tap a bar for detail')}</span>
      </div>

      {/* Coverage disclosure. The line simply stops where last year's records
          do; without this the gap reads as "no sales then" instead of "we
          weren't keeping books yet". */}
      {partialCmp && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-3)' }}>
          {t('sales.noPriorBefore', 'No {y} data before {m} — the comparison starts there.')
            .replace('{y}', String(year - 1))
            .replace('{m}', bucketLabel(buckets[cmpIdx[0]], gran))}
        </div>
      )}
    </div>
  );
}

function SalesTrends({ dark = false, persona = 'owner' }) {
  useT();
  const [st, setSt] = React.useState({ loading: true });
  const [openWeek, setOpenWeek] = React.useState(null); // index of expanded week
  const [period, setPeriod] = React.useState('30'); // '30' | '7'
  const [cfgOpen, setCfgOpen] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false }); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        await window.kanjo.revenue.channels.ensureDefaults();
        try { await window.kanjo.revenue.sales.syncFromDeposits(); } catch (e) { /* best effort */ }
        const profile = await db.userProfile.get(userId).catch(() => null);
        const today = localToday();
        // Reach back to Jan 1 of LAST year: the YTD chart draws this year's
        // buckets against the same periods a year ago, so the prior year has to
        // be in the window. The 60-day rolling cards below read from the same
        // pull. (Local IndexedDB, one query — cheap even at two years of days.)
        const yearNum = Number(today.slice(0, 4));
        const from = `${yearNum - 1}-01-01`;
        const entries = await db.salesEntries.where('[userId+date]').between([userId, from], [userId, today], true, true).toArray();
        const channels = await db.channels.where('userId').equals(userId).toArray();
        const platOf = window.kanjo.revenue.depositRules.deliveryPlatformOf;
        const isDelivery = new Map(channels.map((c) => [c.channelId, !!platOf(c)]));
        const daily = {};
        for (const e of entries) daily[e.date] = (daily[e.date] || 0) + (e.netSalesCents || 0);
        // window sum by day-offset from today (0 = today), inclusive
        const win = (startOff, endOff) => { let s = 0; for (let o = startOff; o <= endOff; o++) s += daily[shiftIsoDate(today, -o)] || 0; return s; };
        // Same window but only delivery channels — feeds the in-house vs
        // delivery slider so it tracks the selected period (7d vs 30d).
        const winDelivery = (startOff, endOff) => {
          let s = 0;
          for (const e of entries) {
            if (!isDelivery.get(e.channelId)) continue;
            const off = Math.round((new Date(today + 'T00:00:00Z') - new Date(e.date + 'T00:00:00Z')) / 86400000);
            if (off >= startOff && off <= endOff) s += e.netSalesCents || 0;
          }
          return s;
        };
        // Last 4 weeks (28 days) split into delivery vs in-house ("dine-in").
        // Per-day totals, then aggregate into the 28-day sum and 4 weekly rows
        // (week 0 = most recent 7 days, offsets 0–6).
        const dailyTotal = {}, dailyDelivery = {};
        for (const e of entries) {
          const n = e.netSalesCents || 0;
          dailyTotal[e.date] = (dailyTotal[e.date] || 0) + n;
          if (isDelivery.get(e.channelId)) dailyDelivery[e.date] = (dailyDelivery[e.date] || 0) + n;
        }
        let w4Total = 0, w4Delivery = 0;
        const w4Weeks = [];
        for (let wk = 0; wk < 4; wk++) {
          let total = 0, delivery = 0;
          const days = [];
          for (let d = 0; d < 7; d++) {
            const iso = shiftIsoDate(today, -(wk * 7 + d));
            const dt = dailyTotal[iso] || 0, dd = dailyDelivery[iso] || 0;
            total += dt; delivery += dd;
            days.push({ date: iso, total: dt, delivery: dd, dineIn: dt - dd }); // newest-first within the week
          }
          w4Total += total; w4Delivery += delivery;
          w4Weeks.push({ end: shiftIsoDate(today, -(wk * 7)), total, delivery, dineIn: total - delivery, days });
        }
        // Year-to-date buckets at all three grain sizes (quarter/month/week),
        // each carrying its same-period-last-year twin. Precomputed here so the
        // chart's toggle is a pure re-render — the day maps above already hold
        // every day of both years.
        const { buildYtdBuckets } = window.kanjo.revenue.salesBuckets;
        const ytd = Object.fromEntries(['week', 'month', 'quarter'].map((g) => [
          g, buildYtdBuckets({ dailyTotal, dailyDelivery, today, granularity: g }),
        ]));

        const dow = Array.from({ length: 7 }, () => ({ sum: 0, count: 0 }));
        for (let o = 0; o <= 59; o++) {
          const d = shiftIsoDate(today, -o);
          const idx = (new Date(d + 'T00:00:00Z').getUTCDay() + 6) % 7; // 0 = Mon
          dow[idx].sum += daily[d] || 0; dow[idx].count++;
        }

        // By-month settled net: credits each boundary deposit to the month its
        // sales were earned (late-month sales deposit in early next month). The
        // delta vs the naive deposit-dated sum is what lands across the boundary.
        const months = [];
        { let m = today.slice(0, 7); for (let i = 0; i < 4; i++) { months.push(m); const [y, mm] = m.split('-').map(Number); const p = new Date(Date.UTC(y, mm - 2, 1)); m = p.toISOString().slice(0, 7); } }
        // settledByMonthDetailed settles each month from its own canonical
        // window — the SAME engine call shape as the Today tile, so the two
        // screens show the same number for the same month. It also carries the
        // in-house/delivery split (feeds the This-month hero) and the raw
        // deposit-dated sum (the "deposits" subtitle).
        const settledDetail = await window.kanjo.revenue.settledSales.settledByMonthDetailed(months).catch(() => ({}));
        const settledMonths = months.map((m) => ({
          month: m,
          netSalesCents: settledDetail[m]?.netSalesCents || 0,
          rawNetSalesCents: settledDetail[m]?.rawNetSalesCents || 0,
          deliveryCents: settledDetail[m]?.deliveryCents || 0,
        }));

        if (!cancelled) setSt({
          loading: false,
          last7: win(0, 6), prior7: win(7, 13),
          last30: win(0, 29), prior30: win(30, 59),
          last7Delivery: winDelivery(0, 6),
          last30Delivery: winDelivery(0, 29),
          w4Total, w4Delivery, w4DineIn: w4Total - w4Delivery, w4Weeks,
          ytd, year: yearNum, settledMonths,
          dow: dow.map((x, i) => ({ label: DOW_LABELS[i], avg: x.count ? Math.round(x.sum / x.count) : 0, total: x.sum })),
          hasData: entries.length > 0,
          currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US',
        });
      } catch (e) { if (!cancelled) setSt({ loading: false, error: String(e?.message || e) }); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (st.loading) return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <KHeader back big title={t('sales.trendsTitle', 'Sales trends')} />
      <div style={{ padding: '0 20px' }}><SkeletonCard height={140}/></div>
    </KShell>
  );

  const M = (c) => fmtMoneyS(c, st.currency, st.locale);
  // Inline KMoney with size baked in — every screen now uses the same
  // big-number / small-grey-cents treatment.
  const $ = (cents, opts = {}) => (
    <KMoney cents={cents} currency={st.currency} locale={st.locale} {...opts}/>
  );

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back big title={t('sales.trendsTitle', 'Sales trends')} subtitle={t('sales.deposits', 'Deposits')}
          right={<GearButton label={t('settings.deposits', 'Deposit recognition settings')} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => setCfgOpen(false)} title={t('settings.deposits', 'Deposit recognition')}>
          <DepositKeywordSettings />
        </Sheet>
        <div style={{ padding: '0 20px' }}>
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {!st.error && !st.hasData && (
            <Empty title={t('sales.noSalesYet', 'No sales yet')}
              body={t('sales.emptyBody', 'Connect a bank or close out a day, and your 7/30-day trends show up here.')} />
          )}
          {!st.error && st.hasData && (
            <>
              {/* Period toggle drives both the hero total and the ratio slider. */}
              <div role="tablist" aria-label={t('sales.periodLabel', 'Period')} style={{ display: 'flex', gap: 4, padding: 4,
                background: 'var(--surface-3,#f0f0f3)', borderRadius: 12, marginBottom: 14 }}>
                {[['month', t('period.thisMonth', 'This month')],
                  ['30', t('period.last30', 'Last 30 days')],
                  ['7', t('period.last7', 'Last 7 days')]].map(([k, lbl]) => {
                  const on = period === k;
                  return (
                    <button key={k} role="tab" aria-selected={on} onClick={() => setPeriod(k)}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none',
                        background: on ? 'var(--surface,#fff)' : 'transparent',
                        color: on ? 'var(--ink)' : 'var(--ink-3)',
                        fontWeight: on ? 600 : 500, fontSize: 13, cursor: 'pointer',
                        boxShadow: on ? '0 1px 2px rgba(0,0,0,0.04)' : 'none' }}>{lbl}</button>
                  );
                })}
              </div>

              {/* Hero: big left-aligned total for the selected period, prior delta
                  inline, then the in-house vs delivery slider with % labels
                  positioned above the breakpoint. */}
              {(() => {
                // 'month' = calendar month-to-date, SETTLED (each boundary
                // deposit credited to the month its sales were earned) — the
                // same figure the Today tile and Cash flow show for the month.
                // The rolling 7/30-day windows stay raw deposit-dated.
                const isMonth = period === 'month';
                const mo = st.settledMonths?.[0];
                const moPrev = st.settledMonths?.[1];
                const total = isMonth ? (mo?.netSalesCents || 0) : period === '7' ? st.last7 : st.last30;
                const delivery = isMonth ? (mo?.deliveryCents || 0) : period === '7' ? st.last7Delivery : st.last30Delivery;
                const prior = isMonth ? (moPrev?.netSalesCents || 0) : period === '7' ? st.prior7 : st.prior30;
                const inHouse = Math.max(0, total - delivery);
                const delPct = total > 0 ? (delivery / total) * 100 : 0;
                const inPct = total > 0 ? 100 - delPct : 0;
                // Month-to-date vs a FULL prior month isn't a fair delta — no arrow there.
                const pct = !isMonth && prior > 0 ? ((total - prior) / prior) * 100 : null;
                const up = pct != null && pct > 0;
                return (
                  <div className="card" style={{ padding: 22, marginBottom: 14 }}>
                    <div className="label">{t('sales.deposits', 'Deposits')} · {isMonth
                      ? t('sales.heroThisMonth', 'this month · settled')
                      : period === '7' ? t('sales.heroLast7', 'last 7 days') : t('sales.heroLast30', 'last 30 days')}</div>
                    <div style={{ marginTop: 8 }}>{$(total, { size: 'xl', tone: 'positive' })}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6 }}>
                      {isMonth ? t('sales.lastMonthFull', 'last month (full):') : t('sales.prior', 'prior:')}{' '}{$(prior, { style: { fontSize: 12.5, color: 'var(--ink-3)' } })}
                      {pct != null && (
                        <span style={{ marginLeft: 6, fontWeight: 700, color: up ? 'var(--positive,#2e9e5b)' : 'var(--danger,#d2563f)' }}>
                          {up ? '▲' : '▼'} {fmtPct1(Math.abs(pct))}
                        </span>
                      )}
                    </div>

                    {/* In-house vs delivery ratio slider. The thumb sits at the
                        in-house/delivery boundary; the percentages float just
                        above the thumb so the split reads at a glance. */}
                    <div style={{ marginTop: 22 }}>
                      <div style={{ position: 'relative', height: 22, marginBottom: 4 }}>
                        <div style={{ position: 'absolute', left: `calc(${inPct}% - 42px)` }}>
                          <KPct value={inPct} tone="positive" style={{ fontSize: 13, fontWeight: 700 }}/>
                        </div>
                        <div style={{ position: 'absolute', left: `calc(${inPct}% + 6px)` }}>
                          <KPct value={delPct} style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)' }}/>
                        </div>
                      </div>
                      <div style={{ position: 'relative', height: 10, borderRadius: 5, overflow: 'hidden',
                        background: 'var(--brand)' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: `${inPct}%`, background: 'var(--positive,#2e9e5b)' }} />
                        <div style={{ position: 'absolute', left: `calc(${inPct}% - 1px)`, top: -3, bottom: -3,
                          width: 2, background: 'var(--ink)', borderRadius: 1 }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8,
                        fontSize: 12, color: 'var(--ink-2)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--positive,#2e9e5b)' }}/>
                          {t('today.inHouse', 'In-house')} {$(inHouse, { style: { fontSize: 12, color: 'var(--ink-3)' } })}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {$(delivery, { style: { fontSize: 12, color: 'var(--ink-3)' } })} {t('today.delivery', 'Delivery')}
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--brand)' }}/>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* By month · settled — credits each boundary deposit to the
                  month its sales were earned. The "adj" line is the cash that
                  crosses the month boundary (late-month sales deposit in early
                  next month), so the monthly total reflects what was earned, not
                  merely what posted. */}
              {st.settledMonths && st.settledMonths.some((mo) => mo.netSalesCents || mo.rawNetSalesCents) && (
                <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{ padding: '14px 16px 6px' }}>
                    <div className="label">{t('sales.byMonthSettled', 'By month · settled')}</div>
                  </div>
                  {st.settledMonths.map((mo, i) => {
                    const adj = (mo.netSalesCents || 0) - (mo.rawNetSalesCents || 0);
                    const label = new Date(mo.month + '-01T00:00:00Z')
                      .toLocaleDateString(uiLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' });
                    return (
                      <div key={mo.month} style={{
                        display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
                        padding: '12px 16px', borderTop: '1px solid var(--rule)',
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-2)' }}>
                            {label}{i === 0 ? ` · ${t('sales.toDate', 'to date')}` : ''}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
                            {t('sales.depositsLower', 'deposits')} {$(mo.rawNetSalesCents, { style: { fontSize: 11.5, color: 'var(--ink-3)' } })}
                            {adj !== 0 && (
                              <span style={{ marginLeft: 6, color: adj > 0 ? 'var(--positive,#2e9e5b)' : 'var(--danger,#d2563f)', fontWeight: 600 }}>
                                {adj > 0 ? '+' : '−'}{M(Math.abs(adj))} {adj > 0
                                  ? t('sales.earnedLandsNext', 'earned, lands next month')
                                  : t('sales.shiftedOut', 'shifted out')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>{$(mo.netSalesCents, { style: { fontSize: 16, fontWeight: 700 } })}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Per-week breakdown — chev on the right for consistency with
                  other report drill-ins. Tap a row to expand its daily detail. */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ padding: '14px 16px 6px' }}>
                  <div className="label">{t('sales.weeklyBreakdown', 'Weekly breakdown · last 4 weeks')}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto 24px', gap: 10,
                  fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, padding: '4px 16px 8px' }}>
                  <span>{t('period.week', 'Week')}</span>
                  <span style={{ textAlign: 'right' }}>{t('sales.colTotal', 'Total')}</span>
                  <span style={{ textAlign: 'right' }}>{t('sales.colDineIn', 'Dine-in')}</span>
                  <span style={{ textAlign: 'right' }}>{t('today.delivery', 'Delivery')}</span>
                  <span style={{ textAlign: 'right', minWidth: 34 }}>{t('sales.colDelPct', 'Del %')}</span>
                  <span/>
                </div>
                {st.w4Weeks.map((w, i) => {
                  const open = openWeek === i;
                  return (
                    <div key={w.end} style={{ borderTop: '1px solid var(--rule)' }}>
                      <button onClick={() => setOpenWeek(open ? null : i)} aria-expanded={open}
                        style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
                          display: 'grid', gridTemplateColumns: '1fr auto auto auto auto 24px', gap: 10,
                          alignItems: 'center', padding: '12px 16px', textAlign: 'left' }}>
                        <span style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500 }}>
                          {i === 0 ? t('sales.thisWeek', 'This week')
                            : t('sales.weekOf', 'Week of {d}').replace('{d}', fmtWeekStart(w.end))}
                        </span>
                        <span style={{ textAlign: 'right' }}>{$(w.total, { style: { fontSize: 12.5, fontWeight: 600 } })}</span>
                        <span style={{ textAlign: 'right' }}>{$(w.dineIn, { style: { fontSize: 12.5, color: 'var(--ink-2)' } })}</span>
                        <span style={{ textAlign: 'right' }}>{$(w.delivery, { style: { fontSize: 12.5, color: 'var(--ink-2)' } })}</span>
                        <span style={{ textAlign: 'right', minWidth: 34 }}>
                          <KPct value={w.total > 0 ? (w.delivery / w.total) * 100 : 0}
                                style={{ fontSize: 12.5, color: 'var(--brand)' }}/>
                        </span>
                        <span style={{ display: 'inline-flex', justifyContent: 'flex-end', color: 'var(--ink-3)',
                          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
                          <Chev size={14} />
                        </span>
                      </button>
                      {open && (
                        <div style={{ padding: '0 16px 10px' }}>
                          {w.days.map((d) => (
                            <div key={d.date} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto 24px', gap: 10,
                              alignItems: 'center', padding: '5px 0 5px 14px' }}>
                              <span style={{ fontSize: 11.5, color: d.total ? 'var(--ink-2)' : 'var(--ink-3)' }}>{fmtDayLabel(d.date)}</span>
                              <span style={{ textAlign: 'right' }}>{d.total ? $(d.total, { style: { fontSize: 11.5 } }) : <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>—</span>}</span>
                              <span style={{ textAlign: 'right' }}>{d.total ? $(d.dineIn, { style: { fontSize: 11.5, color: 'var(--ink-3)' } }) : <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>—</span>}</span>
                              <span style={{ textAlign: 'right' }}>{d.delivery ? $(d.delivery, { style: { fontSize: 11.5, color: 'var(--ink-3)' } }) : <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>—</span>}</span>
                              <span style={{ textAlign: 'right', minWidth: 34 }}>
                                {d.total > 0
                                  ? <KPct value={(d.delivery / d.total) * 100} style={{ fontSize: 11.5, color: 'var(--ink-3)' }}/>
                                  : <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>—</span>}
                              </span>
                              <span/>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* YTD bars by quarter/month/week + last year's same periods. */}
              <YtdTrendChart ytd={st.ytd} year={st.year} currency={st.currency} locale={st.locale} />

              {/* Average-by-weekday chart is hidden for now: with deposits as the
                  source, settlements never post on weekends, so Sat/Sun always
                  read $0 and the "by weekday" view is misleading. Re-enable once a
                  true sale-date feed (POS) exists. The dow data is still computed
                  in the loader (cheap) so re-enabling is just restoring this block.
              <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Average by day of week · last 60 days</div>
              <div className="card" style={{ padding: 18 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 130 }}>
                  {st.dow.map((d) => (
                    <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%', justifyContent: 'flex-end' }}>
                      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{d.avg ? M(d.avg) : ''}</div>
                      <div style={{ width: '100%', display: 'flex', alignItems: 'flex-end', height: 78 }}>
                        <div style={{ width: '100%', height: `${Math.max(2, (d.avg / dowMax) * 100)}%`, background: 'var(--brand)', borderRadius: '4px 4px 0 0', opacity: d.avg ? 1 : 0.25 }}/>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 500 }}>{d.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10 }}>Average daily deposit per weekday, across the last 60 days.</div>
              </div>
              */}
            </>
          )}

          <SalesBasisNote />
        </div>
      </div>
    </KShell>
  );
}

Object.assign(window, { SalesClose, SalesTrends });
